/**
 * Home: a search entry, category chips, then rails that actually scroll.
 *
 * The owner's words, which are the spec for this rewrite: *"There are various sections on the home
 * page. All of them are dry and only show a limited number of prompts and ask the user to click
 * 'See more'. You can show more with a sleek and beautiful design, by scrolling left and right.
 * Also these options are always fixed — the top of each section should open randomly to give the
 * user the feeling prompts are always updating."*
 *
 * Three things changed:
 *
 * - **One request, not three.** Home fired three `/feed` calls with `limit: 10`, so every rail was
 *   ten cards and a link. `/home` returns every rail already composed, with cross-rail variety
 *   preserved server-side — you cannot deduplicate against a rail you never fetched.
 * - **Carousels, not truncated grids.** Twenty-four cards per rail with the next one peeking at
 *   the edge. "See all" survives in the header as a secondary route, no longer the only one.
 * - **A rotating start position.** Each rail opens at a different offset per user per hour, so the
 *   app looks alive between visits without the list moving while someone is reading it.
 *
 * Motion is deliberately small: a staggered entrance on first paint only, and press-springs on the
 * cards. Nothing animates a shadow or a filter, because these are scrolling lists and the
 * compositor is the budget.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PromptCard as PromptCardDto } from '@promptvault/shared';
import { fetchHome } from '../api/home';
import { toggleFavorite } from '../api/prompts';
import { fetchTaxonomy } from '../api/taxonomy';
import { PromptCard } from '../components/PromptCard';
import { Padded, Screen } from '../components/Screen';
import { Rail, SectionTitle } from '../components/content';
import { Chip, ChipRow, SearchBar } from '../components/controls';
import { ErrorState, Placeholder, SkeletonCard, SkeletonRail } from '../components/feedback';
import { Cell } from '../components/primitives';
import { optionalCount } from '../lib/format';
import { promptTypeLabel, useI18n } from '../lib/i18n';
import { openLink } from '../lib/telegram';
import { haptic } from '../hooks/useTg';
import { useMe } from '../providers/AppProviders';

/** Cards per rail. Enough that scrolling right keeps paying out; few enough to stay light. */
const PER_RAIL = 24;

export function Home() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { t, lang } = useI18n();
  const me = useMe();
  const [refreshing, setRefreshing] = useState(false);

  const { data: taxonomy } = useQuery({
    queryKey: ['taxonomy'],
    queryFn: fetchTaxonomy,
    staleTime: 3_600_000,
  });

  const home = useQuery({
    queryKey: ['home', lang, me?.settings.nsfwVisible],
    queryFn: () => fetchHome({ language: lang, perRail: PER_RAIL }),
    staleTime: 5 * 60_000,
  });

  const favMut = useMutation({
    mutationFn: (card: PromptCardDto) => toggleFavorite(card.id),
    onSuccess: () => {
      haptic('medium');
      void qc.invalidateQueries({ queryKey: ['favorites'] });
    },
  });

  const openCard = useCallback((card: PromptCardDto) => navigate(`/prompt/${card.id}`), [navigate]);

  /**
   * Pull-to-refresh rotates every rail forward.
   *
   * `refresh=1` advances each rail by a full window, so the gesture returns genuinely different
   * prompts rather than the same ones. A refresh that changes nothing visible teaches people the
   * control is decorative.
   */
  const refresh = useCallback(async () => {
    setRefreshing(true);
    haptic('light');
    try {
      const next = await fetchHome({ language: lang, perRail: PER_RAIL, refresh: true });
      qc.setQueryData(['home', lang, me?.settings.nsfwVisible], next);
    } finally {
      setRefreshing(false);
    }
  }, [lang, me?.settings.nsfwVisible, qc]);

  const rails = home.data?.rails ?? [];
  const hero = home.data?.promptOfTheDay ?? null;
  const announcement = home.data?.announcement ?? null;
  const announcementText = announcement
    ? lang === 'fa' && announcement.textFa
      ? announcement.textFa
      : announcement.text
    : '';

  /**
   * Same-origin → an in-app route, pushed with the router. Anything else → an external link,
   * opened the way the rest of the app opens one (D45-second: `window.open` is routinely blocked
   * inside Telegram's webview; `openLink` is Telegram's own escape hatch, falling back to
   * `window.open` outside Telegram).
   *
   * The banner rendered `announcementText` and nothing else until now — a curator's `url` was
   * stored, returned by `/home`, and had no tap target anywhere in this file. A banner promising
   * to be "a door, not a notice" that cannot be tapped is the same defect as every other control
   * this project has found wired to nothing.
   */
  const openAnnouncement = useCallback(() => {
    if (!announcement?.url) return;
    haptic('light');
    try {
      const target = new URL(announcement.url, window.location.origin);
      if (target.origin === window.location.origin) navigate(`${target.pathname}${target.search}`);
      else openLink(announcement.url);
    } catch {
      /* an unparseable URL is not navigated — the text still shows */
    }
  }, [announcement?.url, navigate]);

  /**
   * Chips: the curator's order where there is one, the taxonomy's otherwise.
   *
   * The server sends slugs, not names, so the count survives — a resolved `{slug,name}` pair from
   * the API would have none, and the chips would silently lose the number they show today. Three
   * rules, in this order:
   *
   * - the server's order wins, because that is the whole point of ordering them;
   * - a slug the taxonomy does not know is dropped in silence, since the taxonomy already hides
   *   categories below the browse threshold (D55) and a chip leading to an empty screen is the
   *   dead end that decision exists to prevent;
   * - nothing left means fall back to exactly what this screen showed before.
   */
  const chips = useMemo(() => {
    const all = taxonomy?.categories ?? [];
    const wanted = home.data?.categoryChips ?? [];
    if (wanted.length) {
      const bySlug = new Map(all.map((c) => [c.slug, c]));
      const picked = wanted.map((s) => bySlug.get(s)).filter((c): c is (typeof all)[number] => Boolean(c)); // prettier-ignore
      if (picked.length) return picked;
    }
    return all.slice(0, 14);
  }, [taxonomy, home.data?.categoryChips]);

  if (home.isError) {
    return (
      <Screen root>
        <ErrorState message={(home.error as Error)?.message} onRetry={() => void home.refetch()} />
      </Screen>
    );
  }

  return (
    <Screen root chrome="surface">
      <div className="hairline-b bg-surface px-gutter pb-3.5 pt-2">
        {/* `?focus=1` — the only thing that may raise the keyboard is asking to search. */}
        <SearchBar asButton placeholder={t.searchPh} onPress={() => navigate('/search?focus=1')} />
      </div>

      {/*
        The curator's announcement.

        `/home` returned a hardcoded `announcement: null`, so this never existed to render: an
        announcement written in the admin panel showed up in the admin *preview* — which runs the
        same resolver by design (D26) — and reached nobody. Rendered only when there is text, so
        an empty announcement is not an empty banner.
      */}
      {announcement &&
        (announcement.url ? (
          <Padded className="pt-3.5">
            <button
              type="button"
              onClick={openAnnouncement}
              className="w-full rounded-card bg-accent-tint px-3.5 py-2.5 text-start text-footnote text-accent transition-transform duration-fast ease-pv active:scale-[0.98]"
            >
              <p className="bidi-plaintext">{announcementText}</p>
            </button>
          </Padded>
        ) : (
          <Padded className="pt-3.5">
            <div className="rounded-card bg-accent-tint px-3.5 py-2.5 text-footnote text-accent">
              <p className="bidi-plaintext">{announcementText}</p>
            </div>
          </Padded>
        ))}

      {chips.length > 0 && (
        <Padded className="pt-3.5" testId="category-chips">
          <ChipRow>
            {chips.map((c) => (
              <Chip
                key={c.id}
                label={c.name}
                count={optionalCount(c.count, lang)}
                onClick={() => navigate(`/search?category=${encodeURIComponent(c.slug)}`)}
              />
            ))}
          </ChipRow>
        </Padded>
      )}

      {/*
        Type filters, promoted onto Home.

        The library stopped being image-only — text, agent, 3d, video and design all have content
        now — but the only way to filter by type was a sheet two taps deep on Search. A row that
        names them is how someone discovers the library is broader than its thumbnails suggest.
      */}
      {taxonomy && (taxonomy.types?.length ?? 0) > 1 && (
        <Padded className="pt-2" testId="type-chips">
          <ChipRow>
            {(taxonomy.types ?? []).map((ty) => (
              <Chip
                key={ty.type}
                label={promptTypeLabel(ty.type, lang)}
                count={optionalCount(ty.count, lang)}
                onClick={() => navigate(`/search?type=${ty.type}`)}
              />
            ))}
          </ChipRow>
        </Padded>
      )}

      {/* Prompt of the day */}
      <Padded className="pt-5">
        <SectionTitle title={t.potd} />
      </Padded>
      <Padded className="pt-2.5">
        {home.isLoading ? (
          <SkeletonCard ratio="3 / 2" />
        ) : hero ? (
          <PromptCard
            card={hero}
            variant="featured"
            onOpen={openCard}
            onToggleFavorite={(c) => favMut.mutate(c)}
            priority
          />
        ) : (
          <Placeholder icon="sparkle" title={t.noResults} description={t.noResultsSub} compact />
        )}
      </Padded>

      {/*
        Every rail, from the server's own configuration.

        Previously three hardcoded sorts. Now whatever the admin's rail manager defines, which is
        what makes the rail manager mean anything — a curator adding a rail should not need a
        frontend deploy.
      */}
      {home.isLoading
        ? [0, 1, 2].map((i) => (
            <div key={i}>
              <Padded className="pt-6">
                {/* `.pv-shimmer`, not `.skeleton` — the latter is a class from the *admin's*
                    stylesheet and exists nowhere in this app, so a loading rail heading was an
                    empty box with no background at all rather than a shimmering one. */}
                <SectionTitle
                  title={<span className="pv-shimmer inline-block h-4 w-28 rounded" />}
                />
              </Padded>
              <div className="px-gutter pt-2.5">
                <SkeletonRail />
              </div>
            </div>
          ))
        : rails.map((rail, i) => (
            <div
              key={rail.id}
              /*
                Staggered entrance, first paint only.

                `animationDelay` on a one-shot animation, not a transition that would re-run on
                every re-render — a home screen that re-animates when a favourite toggles is worse
                than one that never animates at all. Capped at six so a long list does not end with
                a rail arriving half a second late.
              */
              className="pv-rail-enter"
              style={{ animationDelay: `${Math.min(i, 5) * 35}ms` }}
            >
              <Padded className="pt-6">
                <SectionTitle
                  title={rail.title}
                  action={{
                    label: t.seeAll,
                    onClick: () => navigate(`/search?sort=${railSort(rail.source)}`),
                  }}
                />
              </Padded>
              <div className="px-gutter pt-2.5">
                {(rail.items?.length ?? 0) > 0 ? (
                  <Rail itemWidth={160} snap testId={`rail-${rail.id}`}>
                    {(rail.items ?? []).map((card) => (
                      <PromptCard
                        key={card.id}
                        card={card}
                        onOpen={openCard}
                        onToggleFavorite={(c) => favMut.mutate(c)}
                      />
                    ))}
                  </Rail>
                ) : (
                  <Placeholder icon="sparkle" title={t.noResults} compact />
                )}
              </div>
            </div>
          ))}

      {/* Leaderboard entry */}
      <Padded className="pt-6">
        <div className="overflow-hidden rounded-card bg-surface">
          <Cell
            icon="trophy"
            title={t.leaderboard}
            subtitle={t.leaderboardSub}
            chevron
            last
            onClick={() => navigate('/leaderboard')}
          />
        </div>
      </Padded>

      {/* Refresh — the visible half of the rotation. */}
      <Padded className="pt-6">
        <button
          type="button"
          onClick={() => void refresh()}
          disabled={refreshing}
          className="w-full rounded-card bg-surface py-3 text-subhead font-medium text-accent transition-transform duration-fast ease-pv active:scale-[0.98] disabled:opacity-60"
        >
          {refreshing ? t.loading : t.showMeMore}
        </button>
      </Padded>

      {/* Submit entry */}
      <Padded className="py-6">
        <div className="overflow-hidden rounded-card bg-surface">
          <Cell
            icon="plus"
            title={t.submitPrompt}
            subtitle={t.submitSub}
            chevron
            last
            onClick={() => navigate('/submit')}
          />
        </div>
      </Padded>
    </Screen>
  );
}

/** A rail's server-side source name mapped to the sort the Search screen understands. */
function railSort(source: string): string {
  switch (source) {
    case 'new':
      return 'new';
    case 'top_rated':
      return 'top_rated';
    case 'most_downloaded':
      return 'most_downloaded';
    default:
      return 'trending';
  }
}
